import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus, Check, X, RefreshCw, Search, ShieldAlert, Megaphone, Wallet,
  CalendarSync, Clock, Briefcase, UserCheck, FileCheck2, HardHat, GitBranch,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { EmptyState, CardSkeleton } from './common/States';
import { hrOpsAPI, employeeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const DECISION_ROLES = ['workspace_admin', 'admin', 'hr_admin', 'hr_manager'];

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  pending: 'secondary',
  approved: 'default',
  active: 'default',
  clear: 'default',
  acknowledged: 'default',
  closed: 'outline',
  resolved: 'outline',
  cancelled: 'outline',
  archived: 'outline',
  rejected: 'destructive',
  discrepancy: 'destructive',
  open: 'secondary',
  initiated: 'secondary',
  in_progress: 'secondary',
  investigating: 'secondary',
}[s] || 'secondary');

const today = () => new Date().toISOString().slice(0, 10);

/** Section shell: title, an action, and a table that says so when it is empty. */
const Section = ({ icon: Icon, title, description, action, loading, isEmpty, emptyText, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent>
      {loading && <CardSkeleton rows={4} />}
      {!loading && isEmpty && <EmptyState compact title={emptyText} />}
      {!loading && !isEmpty && children}
    </CardContent>
  </Card>
);

const EmployeePicker = ({ employees, value, onChange, placeholder = 'Select employee' }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>
      {employees.map((e) => (
        <SelectItem key={e.employee_id} value={e.employee_id}>
          {e.first_name} {e.last_name} · {e.employee_id}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const HROperationsModule = () => {
  const { user } = useAuth();
  const canDecide = DECISION_ROLES.includes(String(user?.role || '').toLowerCase());

  const [tab, setTab] = useState('requisitions');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [dialog, setDialog] = useState(null); // { kind, form }

  useEffect(() => {
    employeeAPI.list()
      .then((res) => setEmployees(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setEmployees([]));
  }, []);

  // Each tab owns one loader. A section whose endpoint 403s renders as empty
  // rather than as an error — an unlicensed or ungranted module should look
  // absent, not broken.
  const LOADERS = useMemo(() => ({
    requisitions: () => hrOpsAPI.listRequisitions(),
    'roster-swaps': () => hrOpsAPI.listRosterSwaps(),
    overtime: () => hrOpsAPI.listOvertime(),
    encashments: () => hrOpsAPI.listEncashments(),
    absence: () => hrOpsAPI.listAbsenceEscalations({ status: 'open' }),
    loans: () => hrOpsAPI.listLoans(),
    succession: () => hrOpsAPI.listSuccessionPlans(),
    bgv: () => hrOpsAPI.listBgv(),
    safety: () => hrOpsAPI.listSafetyRecords(),
    announcements: () => hrOpsAPI.listAnnouncements({ include_drafts: true }),
  }), []);

  const load = useCallback(async (which) => {
    const loader = LOADERS[which];
    if (!loader) return;
    setLoading(true);
    try {
      const res = await loader();
      setData((prev) => ({ ...prev, [which]: res.data || [] }));
    } catch {
      setData((prev) => ({ ...prev, [which]: [] }));
    } finally {
      setLoading(false);
    }
  }, [LOADERS]);

  useEffect(() => { load(tab); }, [tab, load]);

  const rows = data[tab] || [];

  const act = async (fn, successMessage) => {
    try {
      await fn();
      toast.success(successMessage);
      setDialog(null);
      load(tab);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not complete that action');
    }
  };

  const openDialog = (kind, form = {}) => setDialog({ kind, form });
  const setField = (key, value) =>
    setDialog((d) => ({ ...d, form: { ...d.form, [key]: value } }));

  const DecisionButtons = ({ onApprove, onReject, disabled }) => (
    <div className="flex justify-end gap-2">
      <Button size="sm" variant="outline" disabled={disabled} onClick={onReject}>
        <X className="h-3.5 w-3.5 mr-1" /> Reject
      </Button>
      <Button size="sm" disabled={disabled} onClick={onApprove}>
        <Check className="h-3.5 w-3.5 mr-1" /> Approve
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="hr-operations-module">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-hr-h1 font-bold">HR Operations</h1>
          <p className="text-muted-foreground">
            Requisitions, rosters, pay requests, compliance and communication.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(tab)}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex w-full flex-wrap justify-start gap-1 h-auto">
          <TabsTrigger value="requisitions">Requisitions</TabsTrigger>
          <TabsTrigger value="roster-swaps">Roster Swaps</TabsTrigger>
          <TabsTrigger value="overtime">Overtime</TabsTrigger>
          <TabsTrigger value="encashments">Encashment</TabsTrigger>
          <TabsTrigger value="absence">Absence</TabsTrigger>
          <TabsTrigger value="loans">Loans</TabsTrigger>
          <TabsTrigger value="succession">Succession</TabsTrigger>
          <TabsTrigger value="bgv">Verification</TabsTrigger>
          <TabsTrigger value="safety">Health &amp; Safety</TabsTrigger>
          <TabsTrigger value="announcements">Announcements</TabsTrigger>
        </TabsList>

        {/* ---------------------------------------------------- Requisitions */}
        <TabsContent value="requisitions" className="mt-4">
          <Section
            icon={Briefcase}
            title="Headcount requisitions"
            description="Authorise a position before it becomes a job posting."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No requisitions raised"
            action={(
              <Button size="sm" onClick={() => openDialog('requisition', { positions: 1, employment_type: 'full_time' })}>
                <Plus className="h-4 w-4 mr-1" /> Raise
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="text-right">Positions</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell>{r.department || '—'}</TableCell>
                    <TableCell className="text-right">{r.positions}</TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell>
                      {canDecide && r.status === 'pending' && (
                        <DecisionButtons
                          onApprove={() => act(() => hrOpsAPI.decideRequisition(r.id, { approve: true }), 'Requisition approved')}
                          onReject={() => act(() => hrOpsAPI.decideRequisition(r.id, { approve: false, reason: 'Not approved' }), 'Requisition rejected')}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* ---------------------------------------------------- Roster swaps */}
        <TabsContent value="roster-swaps" className="mt-4">
          <Section
            icon={CalendarSync}
            title="Roster swaps"
            description="A swap needs the other person's acceptance before it can be approved."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No swap requests"
            action={(
              <Button size="sm" onClick={() => openDialog('swap', { swap_date: today() })}>
                <Plus className="h-4 w-4 mr-1" /> Raise
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Requester</TableHead>
                  <TableHead>With</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Accepted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.requester_employee_id}</TableCell>
                    <TableCell>{r.counterparty_employee_id}</TableCell>
                    <TableCell>{r.swap_date}</TableCell>
                    <TableCell>
                      <Badge variant={r.counterparty_accepted ? 'default' : 'secondary'}>
                        {r.counterparty_accepted ? 'Accepted' : 'Waiting'}
                      </Badge>
                    </TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell>
                      {canDecide && r.status === 'pending' && (
                        <DecisionButtons
                          disabled={!r.counterparty_accepted}
                          onApprove={() => act(() => hrOpsAPI.decideRosterSwap(r.id, { approve: true }), 'Swap approved')}
                          onReject={() => act(() => hrOpsAPI.decideRosterSwap(r.id, { approve: false, reason: 'Not approved' }), 'Swap rejected')}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* -------------------------------------------------------- Overtime */}
        <TabsContent value="overtime" className="mt-4">
          <Section
            icon={Clock}
            title="Overtime approval"
            description="Extra minutes are only paid once somebody approves them."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No overtime claims"
            action={(
              <Button size="sm" onClick={() => openDialog('overtime', { work_date: today(), claimed_minutes: 60 })}>
                <Plus className="h-4 w-4 mr-1" /> Claim
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Claimed</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell>{r.work_date}</TableCell>
                    <TableCell className="text-right">{r.claimed_minutes} min</TableCell>
                    <TableCell className="text-right">
                      {r.approved_minutes === null || r.approved_minutes === undefined ? '—' : `${r.approved_minutes} min`}
                    </TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell>
                      {canDecide && r.status === 'pending' && (
                        <DecisionButtons
                          onApprove={() => act(() => hrOpsAPI.decideOvertime(r.id, { approve: true }), 'Overtime approved')}
                          onReject={() => act(() => hrOpsAPI.decideOvertime(r.id, { approve: false, reason: 'Not approved' }), 'Overtime rejected')}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* ----------------------------------------------------- Encashment */}
        <TabsContent value="encashments" className="mt-4">
          <Section
            icon={Wallet}
            title="Leave encashment"
            description="Encashing leave while still employed. Exit settlements stay in Exit Management."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No encashment requests"
            action={(
              <Button size="sm" onClick={() => openDialog('encashment', { leave_type: 'annual', days: 1 })}>
                <Plus className="h-4 w-4 mr-1" /> Raise
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Leave type</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell>{titleize(r.leave_type)}</TableCell>
                    <TableCell className="text-right">{r.days}</TableCell>
                    <TableCell className="text-right">{r.amount ?? '—'}</TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell>
                      {canDecide && r.status === 'pending' && (
                        <DecisionButtons
                          onApprove={() => act(() => hrOpsAPI.decideEncashment(r.id, { approve: true }), 'Encashment approved')}
                          onReject={() => act(() => hrOpsAPI.decideEncashment(r.id, { approve: false, reason: 'Not approved' }), 'Encashment rejected')}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* --------------------------------------------------------- Absence */}
        <TabsContent value="absence" className="mt-4">
          <Section
            icon={ShieldAlert}
            title="Absence escalations"
            description="People absent several days running with no leave covering it."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="Nobody is unaccounted for"
            action={(
              <Button size="sm" variant="outline" onClick={() => act(() => hrOpsAPI.scanAbsences(3), 'Scan complete')}>
                <Search className="h-4 w-4 mr-1" /> Scan
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead className="text-right">Resolve</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell>{r.first_absent_date}</TableCell>
                    <TableCell>{r.last_absent_date}</TableCell>
                    <TableCell className="text-right">{r.consecutive_days}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDialog('resolve-absence', { id: r.id, resolution: 'leave_applied' })}
                      >
                        Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* ----------------------------------------------------------- Loans */}
        <TabsContent value="loans" className="mt-4">
          <Section
            icon={Wallet}
            title="Loans &amp; advances"
            description="Principal, instalments and outstanding balance."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No loans on record"
            action={canDecide && (
              <Button size="sm" onClick={() => openDialog('loan', { loan_type: 'advance', instalments: 1, interest_rate: 0 })}>
                <Plus className="h-4 w-4 mr-1" /> New
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Principal</TableHead>
                  <TableHead className="text-right">Instalment</TableHead>
                  <TableHead className="text-right">Outstanding</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Decision</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.employee_id}</TableCell>
                    <TableCell>{titleize(r.loan_type)}</TableCell>
                    <TableCell className="text-right">{r.principal}</TableCell>
                    <TableCell className="text-right">{r.instalment_amount}</TableCell>
                    <TableCell className="text-right">{r.outstanding}</TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell>
                      {canDecide && r.status === 'pending' && (
                        <DecisionButtons
                          onApprove={() => act(() => hrOpsAPI.decideLoan(r.id, { approve: true }), 'Loan approved')}
                          onReject={() => act(() => hrOpsAPI.decideLoan(r.id, { approve: false, reason: 'Not approved' }), 'Loan rejected')}
                        />
                      )}
                      {canDecide && r.status === 'active' && (
                        <div className="flex justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDialog('repayment', { id: r.id, amount: r.instalment_amount })}
                          >
                            Record repayment
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* ------------------------------------------------------ Succession */}
        <TabsContent value="succession" className="mt-4">
          <Section
            icon={GitBranch}
            title="Succession planning"
            description="Who would take a critical role, and how ready they are."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No succession plans"
            action={(
              <Button size="sm" onClick={() => openDialog('succession', { criticality: 'medium', risk_of_loss: 'low' })}>
                <Plus className="h-4 w-4 mr-1" /> New plan
              </Button>
            )}
          >
            <div className="space-y-4">
              {rows.map((p) => (
                <div key={p.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium">{p.position_title}</p>
                      <p className="text-sm text-muted-foreground">
                        {p.department || 'No department'} · Criticality {p.criticality} · Risk {p.risk_of_loss}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDialog('succession-candidate', { plan_id: p.id, readiness: 'develop', rank: (p.candidates?.length || 0) + 1 })}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Candidate
                    </Button>
                  </div>
                  {p.candidates?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {p.candidates.map((c) => (
                        <Badge key={c.id} variant="outline">
                          #{c.rank} {c.employee_id} · {titleize(c.readiness)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        {/* ------------------------------------------------------------ BGV */}
        <TabsContent value="bgv" className="mt-4">
          <Section
            icon={UserCheck}
            title="Background verification"
            description="Cases follow a person from candidate to employee."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No verification cases"
            action={(
              <Button size="sm" onClick={() => openDialog('bgv', { subject_type: 'employee', initiated_on: today() })}>
                <Plus className="h-4 w-4 mr-1" /> New case
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Update</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">
                      {r.subject_name || r.employee_id || `Candidate ${r.candidate_id}`}
                    </TableCell>
                    <TableCell>{r.vendor || '—'}</TableCell>
                    <TableCell>{r.reference_number || '—'}</TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Select value="" onValueChange={(v) => act(() => hrOpsAPI.updateBgv(r.id, { status: v }), 'Case updated')}>
                        <SelectTrigger className="ml-auto w-40"><SelectValue placeholder="Set status" /></SelectTrigger>
                        <SelectContent>
                          {['initiated', 'in_progress', 'clear', 'discrepancy', 'cancelled'].map((s) => (
                            <SelectItem key={s} value={s}>{titleize(s)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* --------------------------------------------------------- Safety */}
        <TabsContent value="safety" className="mt-4">
          <Section
            icon={HardHat}
            title="Health &amp; safety"
            description="Incidents, near misses, inspections and drills."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="No safety records"
            action={(
              <Button size="sm" onClick={() => openDialog('safety', { record_type: 'incident', severity: 'low', occurred_on: today() })}>
                <Plus className="h-4 w-4 mr-1" /> Report
              </Button>
            )}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Occurred</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Close</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.title}</TableCell>
                    <TableCell>{titleize(r.record_type)}</TableCell>
                    <TableCell>{r.occurred_on}</TableCell>
                    <TableCell><Badge variant={r.severity === 'critical' || r.severity === 'high' ? 'destructive' : 'secondary'}>{titleize(r.severity)}</Badge></TableCell>
                    <TableCell><Badge variant={statusVariant(r.status)}>{titleize(r.status)}</Badge></TableCell>
                    <TableCell className="text-right">
                      {r.status !== 'closed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDialog('close-safety', { id: r.id })}
                        >
                          Close
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        </TabsContent>

        {/* -------------------------------------------------- Announcements */}
        <TabsContent value="announcements" className="mt-4">
          <Section
            icon={Megaphone}
            title="Announcements"
            description="A notice to the workspace, a department, or a set of roles."
            loading={loading}
            isEmpty={!rows.length}
            emptyText="Nothing announced yet"
            action={canDecide && (
              <Button size="sm" onClick={() => openDialog('announcement', { category: 'general', status: 'published', is_pinned: false })}>
                <Plus className="h-4 w-4 mr-1" /> Write
              </Button>
            )}
          >
            <div className="space-y-3">
              {rows.map((a) => (
                <div key={a.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{a.title}</p>
                        {a.is_pinned && <Badge variant="outline">Pinned</Badge>}
                        <Badge variant={statusVariant(a.status)}>{titleize(a.status)}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-line">{a.body}</p>
                      <p className="text-xs text-muted-foreground">
                        {titleize(a.category)}
                        {a.audience_departments?.length ? ` · ${a.audience_departments.join(', ')}` : ' · Everyone'}
                      </p>
                    </div>
                    {canDecide && a.status !== 'archived' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => act(() => hrOpsAPI.archiveAnnouncement(a.id), 'Announcement archived')}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      {/* ------------------------------------------------------------ Forms */}
      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {{
                requisition: 'Raise a requisition',
                swap: 'Request a roster swap',
                overtime: 'Claim overtime',
                encashment: 'Request leave encashment',
                'resolve-absence': 'Resolve absence',
                loan: 'New loan or advance',
                repayment: 'Record a repayment',
                succession: 'New succession plan',
                'succession-candidate': 'Add a candidate',
                bgv: 'Start a verification',
                safety: 'Report a safety record',
                'close-safety': 'Close safety record',
                announcement: 'Write an announcement',
              }[dialog?.kind] || ''}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {dialog?.kind === 'requisition' && (
              <>
                <div className="space-y-2">
                  <Label>Role title</Label>
                  <Input value={dialog.form.title || ''} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Input value={dialog.form.department || ''} onChange={(e) => setField('department', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Positions</Label>
                    <Input
                      type="number"
                      min={1}
                      value={dialog.form.positions}
                      onChange={(e) => setField('positions', Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Justification</Label>
                  <Textarea value={dialog.form.justification || ''} onChange={(e) => setField('justification', e.target.value)} />
                </div>
              </>
            )}

            {dialog?.kind === 'swap' && (
              <>
                <div className="space-y-2">
                  <Label>Requester (leave blank to request for yourself)</Label>
                  <EmployeePicker
                    employees={employees}
                    value={dialog.form.requester_employee_id}
                    onChange={(v) => setField('requester_employee_id', v)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Swapping with</Label>
                  <EmployeePicker
                    employees={employees}
                    value={dialog.form.counterparty_employee_id}
                    onChange={(v) => setField('counterparty_employee_id', v)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={dialog.form.swap_date || ''} onChange={(e) => setField('swap_date', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea value={dialog.form.reason || ''} onChange={(e) => setField('reason', e.target.value)} />
                </div>
              </>
            )}

            {dialog?.kind === 'overtime' && (
              <>
                <div className="space-y-2">
                  <Label>Employee (leave blank to claim for yourself)</Label>
                  <EmployeePicker employees={employees} value={dialog.form.employee_id} onChange={(v) => setField('employee_id', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input type="date" value={dialog.form.work_date || ''} onChange={(e) => setField('work_date', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Minutes</Label>
                    <Input
                      type="number"
                      min={1}
                      value={dialog.form.claimed_minutes}
                      onChange={(e) => setField('claimed_minutes', Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea value={dialog.form.reason || ''} onChange={(e) => setField('reason', e.target.value)} />
                </div>
              </>
            )}

            {dialog?.kind === 'encashment' && (
              <>
                <div className="space-y-2">
                  <Label>Employee (leave blank to request for yourself)</Label>
                  <EmployeePicker employees={employees} value={dialog.form.employee_id} onChange={(v) => setField('employee_id', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Leave type</Label>
                    <Input value={dialog.form.leave_type || ''} onChange={(e) => setField('leave_type', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Days</Label>
                    <Input
                      type="number"
                      min={0.5}
                      step={0.5}
                      value={dialog.form.days}
                      onChange={(e) => setField('days', Number(e.target.value))}
                    />
                  </div>
                </div>
              </>
            )}

            {dialog?.kind === 'resolve-absence' && (
              <>
                <div className="space-y-2">
                  <Label>Outcome</Label>
                  <Select value={dialog.form.resolution} onValueChange={(v) => setField('resolution', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['leave_applied', 'regularised', 'disciplinary', 'other'].map((r) => (
                        <SelectItem key={r} value={r}>{titleize(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={dialog.form.notes || ''} onChange={(e) => setField('notes', e.target.value)} />
                </div>
              </>
            )}

            {dialog?.kind === 'loan' && (
              <>
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <EmployeePicker employees={employees} value={dialog.form.employee_id} onChange={(v) => setField('employee_id', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={dialog.form.loan_type} onValueChange={(v) => setField('loan_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['advance', 'personal', 'emergency'].map((t) => (
                          <SelectItem key={t} value={t}>{titleize(t)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Principal</Label>
                    <Input
                      type="number"
                      min={1}
                      value={dialog.form.principal || ''}
                      onChange={(e) => setField('principal', Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Instalments</Label>
                    <Input
                      type="number"
                      min={1}
                      value={dialog.form.instalments}
                      onChange={(e) => setField('instalments', Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interest %</Label>
                    <Input
                      type="number"
                      min={0}
                      step={0.1}
                      value={dialog.form.interest_rate}
                      onChange={(e) => setField('interest_rate', Number(e.target.value))}
                    />
                  </div>
                </div>
              </>
            )}

            {dialog?.kind === 'repayment' && (
              <div className="space-y-2">
                <Label>Amount</Label>
                <Input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={dialog.form.amount || ''}
                  onChange={(e) => setField('amount', Number(e.target.value))}
                />
              </div>
            )}

            {dialog?.kind === 'succession' && (
              <>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input value={dialog.form.position_title || ''} onChange={(e) => setField('position_title', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Criticality</Label>
                    <Select value={dialog.form.criticality} onValueChange={(v) => setField('criticality', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['low', 'medium', 'high'].map((v) => <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Risk of loss</Label>
                    <Select value={dialog.form.risk_of_loss} onValueChange={(v) => setField('risk_of_loss', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['low', 'medium', 'high'].map((v) => <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Incumbent</Label>
                  <EmployeePicker employees={employees} value={dialog.form.incumbent_employee_id} onChange={(v) => setField('incumbent_employee_id', v)} />
                </div>
              </>
            )}

            {dialog?.kind === 'succession-candidate' && (
              <>
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <EmployeePicker employees={employees} value={dialog.form.employee_id} onChange={(v) => setField('employee_id', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Readiness</Label>
                    <Select value={dialog.form.readiness} onValueChange={(v) => setField('readiness', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['ready_now', 'ready_1_2_years', 'develop'].map((v) => (
                          <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Rank</Label>
                    <Input
                      type="number"
                      min={1}
                      value={dialog.form.rank}
                      onChange={(e) => setField('rank', Number(e.target.value))}
                    />
                  </div>
                </div>
              </>
            )}

            {dialog?.kind === 'bgv' && (
              <>
                <div className="space-y-2">
                  <Label>Employee</Label>
                  <EmployeePicker employees={employees} value={dialog.form.employee_id} onChange={(v) => setField('employee_id', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Input value={dialog.form.vendor || ''} onChange={(e) => setField('vendor', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Reference</Label>
                    <Input value={dialog.form.reference_number || ''} onChange={(e) => setField('reference_number', e.target.value)} />
                  </div>
                </div>
              </>
            )}

            {dialog?.kind === 'safety' && (
              <>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input value={dialog.form.title || ''} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={dialog.form.record_type} onValueChange={(v) => setField('record_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['incident', 'near_miss', 'inspection', 'training', 'drill'].map((v) => (
                          <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Severity</Label>
                    <Select value={dialog.form.severity} onValueChange={(v) => setField('severity', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['low', 'medium', 'high', 'critical'].map((v) => (
                          <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Occurred on</Label>
                  <Input type="date" value={dialog.form.occurred_on || ''} onChange={(e) => setField('occurred_on', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>What happened</Label>
                  <Textarea value={dialog.form.description || ''} onChange={(e) => setField('description', e.target.value)} />
                </div>
              </>
            )}

            {dialog?.kind === 'close-safety' && (
              <div className="space-y-2">
                <Label>Corrective action</Label>
                <Textarea value={dialog.form.corrective_action || ''} onChange={(e) => setField('corrective_action', e.target.value)} />
              </div>
            )}

            {dialog?.kind === 'announcement' && (
              <>
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input value={dialog.form.title || ''} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea rows={5} value={dialog.form.body || ''} onChange={(e) => setField('body', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select value={dialog.form.category} onValueChange={(v) => setField('category', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['general', 'policy', 'event', 'urgent'].map((v) => (
                          <SelectItem key={v} value={v}>{titleize(v)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2 pb-2">
                    <Switch
                      id="pin-announcement"
                      checked={Boolean(dialog.form.is_pinned)}
                      onCheckedChange={(v) => setField('is_pinned', v)}
                    />
                    <Label htmlFor="pin-announcement">Pin to the top</Label>
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const { kind, form } = dialog;
                const submit = {
                  requisition: () => hrOpsAPI.createRequisition(form),
                  swap: () => hrOpsAPI.createRosterSwap(form),
                  overtime: () => hrOpsAPI.claimOvertime(form),
                  encashment: () => hrOpsAPI.createEncashment(form),
                  'resolve-absence': () => hrOpsAPI.resolveAbsence(form.id, { resolution: form.resolution, notes: form.notes }),
                  loan: () => hrOpsAPI.createLoan(form),
                  repayment: () => hrOpsAPI.recordRepayment(form.id, { amount: form.amount }),
                  succession: () => hrOpsAPI.createSuccessionPlan(form),
                  'succession-candidate': () => hrOpsAPI.addSuccessionCandidate(form.plan_id, form),
                  bgv: () => hrOpsAPI.createBgv(form),
                  safety: () => hrOpsAPI.createSafetyRecord(form),
                  'close-safety': () => hrOpsAPI.updateSafetyRecord(form.id, { status: 'closed', corrective_action: form.corrective_action }),
                  announcement: () => hrOpsAPI.createAnnouncement(form),
                }[kind];
                if (submit) act(submit, 'Saved');
              }}
            >
              <FileCheck2 className="h-4 w-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HROperationsModule;
